import PropTypes from 'prop-types'
import { Tab, Tabs } from 'baseui/tabs-motion'
import ContactTab from './Tabs/Contact'
import useSubscriptionForm from './useSubscriptionForm'
import MeterIds from './Tabs/MeterIds'
import { Delete } from 'baseui/icon'
import { useSelector } from 'react-redux'
import useTabBar from './useTabBar'
import useTabBarStyle from './useTabBarStyle'
import usePulseTabsOverflow from './usePulseTabsOverflow'
import useClientTabRedesign from './useClientTabRedesign'
import useAdditionalSalesRedesign from './useAdditionalSalesRedesign'
import SaleForm from '../Layouts/SaleForm'
import { ErrorBoundary } from 'react-error-boundary'
import { Suspense } from 'react'
import Addresses from './Tabs/Addresses'
import CreateContractModal from '@/containers/SubscriptionFormPageNew/Components/CreateContractModal/CreateContractModal'
import { Button } from 'baseui/button'
import useCreateContractModal from '../Components/CreateContractModal/useCreateContractModal'
import AdditionalSalesContainer from '../Components/AdditionalSales/AdditionalSalesContainer'
import AdditionalSalesV2Container from '../Components/AdditionalSalesV2/AdditionalSalesV2Container'
import ClientTopHeader from '../Layouts/ClientTopHeader'
import { selectClientTabHasUnsavedChanges } from './SubscriptionFormSelectors'
import CopyContractLink from './CopyContractLink'
import UnsavedChangesBadge from './UnsavedChangesBadge/UnsavedChangesBadge'
import RightPanel from '@/containers/SubscriptionFormPageNew/Components/shared/RightPanel/RightPanel'
import RightPanelProvider from '@/containers/SubscriptionFormPageNew/Components/shared/RightPanel/RightPanelProvider'
import LegacyRightPanel from '@/containers/SubscriptionFormPageNew/Components/LegacyRightPanel/LegacyRightPanel'
import PulseScope from '@/shared/components/PulseScope/PulseScope'
import ClientTabSaveArea from './ClientTabSaveArea/ClientTabSaveArea'
import PulseClientTabTitle from './PulseClientTabTitle/PulseClientTabTitle'
import useClientTabColumns from './useClientTabColumns'
import ClientTabStatusProvider from './ClientTabStatus/ClientTabStatusProvider'
import ClientTabStatusChips from './ClientTabStatus/ClientTabStatusChips'
import IconSell from '@/shared/icons/NewDesign/IconSell'
import PulseTabLabel from './PulseTabLabel/PulseTabLabel'
import PulseContractTabIcon from './PulseTabLabel/PulseContractTabIcon'
import {
  ClientTabColumns,
  ClientTabSideColumns,
  PulseClientToolbar,
  PulseTabClose,
  TabsFrame,
} from './styled-components'

const SubscriptionForm = ({ country, id }) => {
  const {
    activeTab,
    openedContracts,
    existingContracts,
    removeFromOpenedContracts,
    keepTabInactive,
    closeContractTab,
    closeContractTabOnKey,
    updateActiveTabHandler,
    t,
  } = useTabBar()
  const clientTabHasUnsavedChanges = useSelector(selectClientTabHasUnsavedChanges)

  // 50%-alpha diagonal orange/yellow stripes used to flag unsaved changes
  // on the Client tab. The 50% alpha lets the underlying tab color tint
  // through (white when active, lightgrey when inactive).
  const dirtyStripesBackground =
    'repeating-linear-gradient(45deg, rgba(255, 232, 168, 0.5) 0, rgba(255, 232, 168, 0.5) 8px, rgba(255, 179, 71, 0.5) 8px, rgba(255, 179, 71, 0.5) 16px)'

  const isClientTabRedesign = useClientTabRedesign()
  const isAdditionalSalesRedesign = useAdditionalSalesRedesign()
  const { frameRef: tabsFrameRef, overflowing: tabsOverflowing } = usePulseTabsOverflow(
    isClientTabRedesign,
    openedContracts.length,
  )

  const {
    isPulse,
    tabStyle,
    additionalSalesTabStyle,
    clientTabStyle,
    contractTabStyle,
    tabsOverrides,
    mainContainerStyle,
    tabsContainerStyle,
    addButtonStyle,
  } = useTabBarStyle({ clientTabHasUnsavedChanges, tabsOverflowing, activeTab })
  const { columnsRef, columns } = useClientTabColumns(isPulse)

  const { toggleContractModal } = useCreateContractModal()

  useSubscriptionForm(id)

  return (
    <RightPanelProvider>
      <div style={mainContainerStyle}>
        <PulseScope enabled={isPulse}>
          <CreateContractModal />
        </PulseScope>
        <TabsFrame ref={tabsFrameRef} $pulse={isPulse} style={tabsContainerStyle} onKeyDown={closeContractTabOnKey}>
          <Tabs
            activeKey={activeTab}
            onChange={({ activeKey }) => updateActiveTabHandler(activeKey)}
            overrides={tabsOverrides}
            endEnhancer={
              <PulseScope enabled={isPulse}>
                <Button
                  onClick={event => {
                    event.preventDefault()
                    toggleContractModal()
                  }}
                  overrides={addButtonStyle}
                >
                  {t('subscriptionForm:contract_modal.tab_title')}
                </Button>
              </PulseScope>
            }
          >
            <Tab
              title={
                isPulse ? (
                  <PulseClientTabTitle active={activeTab === 'client'} />
                ) : (
                  <span
                    style={{
                      position: 'relative',
                      display: 'inline-flex',
                      alignItems: 'center',
                      // Right-side breathing room for the absolutely-positioned
                      // badge so the next tab's button doesn't overlap it.
                      paddingRight: clientTabHasUnsavedChanges ? '24px' : 0,
                    }}
                  >
                    {t('common:client')}
                    <UnsavedChangesBadge />
                  </span>
                )
              }
              key='client'
              overrides={
                clientTabStyle ?? {
                  ...tabStyle,
                  Tab: {
                    style: ({ $isActive }) => ({
                      ...tabStyle.Tab.style({ $isActive }),
                      ...(clientTabHasUnsavedChanges && {
                        backgroundImage: dirtyStripesBackground,
                        backgroundColor: $isActive ? 'white' : 'lightgrey',
                        ':hover': {
                          backgroundImage: dirtyStripesBackground,
                          backgroundColor: $isActive ? 'white' : 'lightgrey',
                        },
                        ':focus': {
                          backgroundImage: dirtyStripesBackground,
                          backgroundColor: $isActive ? 'white' : 'lightgrey',
                        },
                      }),
                    }),
                  },
                }
              }
            >
              <PulseScope enabled={isPulse}>
                <ClientTabStatusProvider enabled={isPulse}>
                  {/* Column wrapper: save bar on top, modules row below. */}
                  <div
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <ClientTopHeader hidden={isPulse} />
                    <PulseClientToolbar $hidden={!isPulse}>
                      <ClientTabStatusChips hidden={!isPulse} />
                      <ClientTabSaveArea hidden={!isPulse} />
                    </PulseClientToolbar>
                    <ClientTabColumns ref={columnsRef} $pulse={isPulse} $columns={columns}>
                      <ContactTab />
                      <ClientTabSideColumns $stacked={isPulse && columns === 2}>
                        <Addresses />
                        <MeterIds />
                      </ClientTabSideColumns>
                    </ClientTabColumns>
                  </div>
                </ClientTabStatusProvider>
              </PulseScope>
            </Tab>
            {isPulse && (
              <Tab
                title={
                  <PulseTabLabel Icon={IconSell} active={activeTab === 'additional-sales'}>
                    {t('additionalSales:common.tab_title')}
                  </PulseTabLabel>
                }
                key='additional-sales'
                overrides={additionalSalesTabStyle}
              >
                {isAdditionalSalesRedesign ? (
                  <PulseScope enabled>
                    <AdditionalSalesV2Container />
                  </PulseScope>
                ) : (
                  <AdditionalSalesContainer />
                )}
              </Tab>
            )}
            {openedContracts.length > 0 &&
              openedContracts.map(contractId => {
                const contract = existingContracts[contractId]
                if (!contract) {
                  return null
                }

                const contractKey = contract.id + '-' + contract.type
                return (
                  <Tab
                    key={contractKey}
                    data-contract-id={contract.id}
                    aria-keyshortcuts={isPulse ? 'Delete' : undefined}
                    data-contract-type={contract.type}
                    artwork={
                      isPulse
                        ? undefined
                        : () => (
                            <Delete
                              onClick={e => {
                                e.stopPropagation()
                                removeFromOpenedContracts({ id: contract.id, type: contract.type })
                              }}
                            />
                          )
                    }
                    title={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        {isPulse && <PulseContractTabIcon type={contract.type} active={activeTab === contractKey} />}
                        {`${t(`common:${contract.type}`)} ${contract.id}`}
                        {/* Quick-access deep link to this contract (URL + #id), copied to clipboard */}
                        <CopyContractLink
                          value={`${window.location.origin}${window.location.pathname}${window.location.search}#${contract.id}`}
                        />
                        {isPulse && (
                          <PulseTabClose
                            aria-hidden='true'
                            onMouseDown={keepTabInactive}
                            onClick={closeContractTab(contract)}
                          >
                            <Delete size={16} />
                          </PulseTabClose>
                        )}
                      </span>
                    }
                    overrides={contractTabStyle}
                  >
                    <PulseScope enabled={isPulse}>
                      <div style={{ width: '100%', maxWidth: '100%', display: 'flex', flexGrow: 1 }}>
                        <ErrorBoundary fallback={<p>{t('common:error_contact_support')}</p>}>
                          <Suspense fallback={<div>{t('common:loading')}</div>}>
                            <SaleForm country={country} type={contract.type} />
                          </Suspense>
                        </ErrorBoundary>
                      </div>
                    </PulseScope>
                  </Tab>
                )
              })}
            {!isPulse && (
              <Tab title={t('additionalSales:common.tab_title')} key='additional-sales' overrides={tabStyle}>
                <AdditionalSalesContainer />
              </Tab>
            )}
          </Tabs>
        </TabsFrame>
        <RightPanel country={country} />
        <LegacyRightPanel country={country} />
      </div>
    </RightPanelProvider>
  )
}

SubscriptionForm.propTypes = {
  id: PropTypes.string,
  country: PropTypes.string,
}

export default SubscriptionForm
